// src/components/resources/tools/generators/AppStoreScreenshot/AppStoreScreenshotGenerator.tsx

/**
 * @fileoverview App Store / Play Store Screenshot Maker
 * @description Builds marketing screenshots for app-store listings: a captioned,
 * device-framed shot on a branded background, rendered at the **correct store
 * aspect ratio** for the chosen preset (iPhone 6.7"/6.5"/5.5", iPad 12.9",
 * Google Play phone, and the Play feature graphic). Each preset shows its exact
 * target resolution so you can confirm compliance, and exports a high-resolution
 * PNG.
 *
 * Distinct from `screenshot-beautifier` (which polishes a single screenshot for
 * decks/social): this tool targets the App Store Connect / Play Console listing
 * formats specifically — correct aspect ratios, caption-above/below layout, and
 * device frames — so the output drops straight into a store listing.
 *
 * Export fidelity: the PNG is resampled to the EXACT store resolution (e.g.
 * 1290×2796) via the exporter's `exactSize` option — the on-screen preview is
 * captured at a scale large enough to exceed the target, then downscaled to the
 * precise spec, so App Store Connect / Play Console accept it without a manual
 * resize.
 *
 * Demo-trust: defaults are neutral placeholders so an unedited export can't read
 * as a real listing for a real app.
 *
 * @module AppStoreScreenshot
 */

'use client'

import { useCallback, useMemo, useRef, useState, type ChangeEvent } from 'react'
import { toast } from 'sonner'
import { PhotoIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import { clsx } from 'clsx'
import { createEngagementTimers, sendResourceEvent } from '~/lib/gtag'
import { captureAndDownload } from '../shared/exportImage'
import { ExportButton } from '../shared/ExportButton'
import { useExport } from '../shared/useExport'
import { waitForStable } from '../shared/waitForStable'
import { usePreviewDeferral, useStaleRef } from '../shared/usePreviewDeferral'

type FrameKind = 'iphone' | 'android' | 'none'
type CaptionPos = 'top' | 'bottom'

interface Preset {
  id: string
  label: string
  store: string
  /** Target listing resolution in px (portrait unless noted). */
  w: number
  h: number
  frame: FrameKind
  /** Feature graphic is a caption-only landscape banner (no device). */
  banner?: boolean
}

const DEFAULT_PRESET: Preset = { id: 'ios-6.7', label: 'iPhone 6.7″', store: 'App Store', w: 1290, h: 2796, frame: 'iphone' }
const PRESETS: readonly Preset[] = [
  DEFAULT_PRESET,
  { id: 'ios-6.5', label: 'iPhone 6.5″', store: 'App Store', w: 1242, h: 2688, frame: 'iphone' },
  { id: 'ios-5.5', label: 'iPhone 5.5″', store: 'App Store', w: 1242, h: 2208, frame: 'iphone' },
  { id: 'ipad-12.9', label: 'iPad 12.9″', store: 'App Store', w: 2048, h: 2732, frame: 'none' },
  { id: 'play-phone', label: 'Play phone', store: 'Google Play', w: 1080, h: 1920, frame: 'android' },
  { id: 'play-feature', label: 'Play feature graphic', store: 'Google Play', w: 1024, h: 500, frame: 'none', banner: true },
]

interface Background {
  id: string
  label: string
  css: string
}
const DEFAULT_BACKGROUND: Background = { id: 'indigo', label: 'Indigo', css: 'linear-gradient(160deg,#4f46e5 0%,#7c3aed 100%)' }
const BACKGROUNDS: readonly Background[] = [
  DEFAULT_BACKGROUND,
  { id: 'sunset', label: 'Sunset', css: 'linear-gradient(160deg,#f97316 0%,#db2777 100%)' },
  { id: 'mint', label: 'Mint', css: 'linear-gradient(160deg,#10b981 0%,#0ea5e9 100%)' },
  { id: 'slate', label: 'Slate', css: 'linear-gradient(160deg,#0f172a 0%,#334155 100%)' },
  { id: 'paper', label: 'Paper', css: 'linear-gradient(160deg,#f8fafc 0%,#e2e8f0 100%)' },
]

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024

interface ScreenshotState {
  presetId: string
  headline: string
  subheadline: string
  captionPos: CaptionPos
  backgroundId: string
  solidColor: string
  useSolid: boolean
  textColor: string
  showFrame: boolean
}

const DEFAULT_STATE: ScreenshotState = {
  presetId: 'ios-6.7',
  headline: 'Your headline goes here',
  subheadline: 'A short benefit-led subheadline.',
  captionPos: 'top',
  backgroundId: 'indigo',
  solidColor: '#4f46e5',
  useSolid: false,
  textColor: '#ffffff',
  showFrame: true,
}

interface AppStoreScreenshotGeneratorProps {
  trackingSlug?: string
}

export function AppStoreScreenshotGenerator({
  trackingSlug = 'app-store-screenshot-generator',
}: AppStoreScreenshotGeneratorProps): JSX.Element {
  const [state, setState] = useState<ScreenshotState>(DEFAULT_STATE)
  const [shot, setShot] = useState<string | null>(null)
  // Shared export lifecycle: warm html2canvas on mount, re-entrancy guard,
  // and the `isExporting` flag for the Download affordance.
  const { isExporting, runExport } = useExport()
  const previewRef = useRef<HTMLDivElement>(null)
  const [getTotalEngagementMs, getDeltaEngagementMs] = useMemo(() => createEngagementTimers(), [])

  // Render the framed store-screenshot preview from a `useDeferredValue`
  // snapshot so headline/subheadline keystrokes commit synchronously (inputs
  // read live `state`) while the preview re-renders at a lower priority — the
  // same boundary the other generators use to shorten the long interaction task
  // behind the generator pages' mobile INP. Export waits for the snapshot to
  // converge, so the captured raster matches the live preset's exact spec.
  const { deferred: deferredState, isStale: stateStale } = usePreviewDeferral(state)
  const { deferred: deferredShot, isStale: shotStale } = usePreviewDeferral(shot)
  const previewStaleRef = useStaleRef(stateStale || shotStale)

  const preset = PRESETS.find(p => p.id === state.presetId) ?? DEFAULT_PRESET

  // Preview-side preset/background read the deferred snapshot so the wrapper's
  // width, aspect-ratio box, and rendered content stay coherent with each other
  // (a preset switch reaches all three on the same low-priority commit).
  const previewPreset = PRESETS.find(p => p.id === deferredState.presetId) ?? DEFAULT_PRESET
  const previewBackground = deferredState.useSolid ? deferredState.solidColor : (BACKGROUNDS.find(b => b.id === deferredState.backgroundId)?.css ?? DEFAULT_BACKGROUND.css)

  const update = useCallback(<K extends keyof ScreenshotState>(key: K, value: ScreenshotState[K]): void => {
    setState(prev => ({ ...prev, [key]: value }))
  }, [])

  const handleShot = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
    const input = event.target
    const file = input.files?.[0]
    if (!file) return
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error('Image is too large (max 10 MB). Please choose a smaller file.')
      input.value = ''
      return
    }
    const reader = new FileReader()
    reader.onerror = () => { toast.error('Could not read that image. Please try another file.'); input.value = '' }
    reader.onload = () => {
      setShot(typeof reader.result === 'string' ? reader.result : null)
      sendResourceEvent('interact', {
        resourceType: 'tool',
        resourceSlug: trackingSlug,
        engagementMs: getDeltaEngagementMs(),
        params: { tool_type: 'generator', interaction_type: 'upload_screenshot', file_type: file.type, file_size: file.size },
      })
    }
    reader.readAsDataURL(file)
  }, [getDeltaEngagementMs, trackingSlug])

  const reset = useCallback((): void => { setState(DEFAULT_STATE); setShot(null) }, [])

  const exportPng = useCallback((): Promise<void> => runExport(async beginCapture => {
    if (!previewRef.current) { toast.error('Preview not found.'); return }
    // Preview renders from `deferredState`; wait for it to converge so the
    // captured DOM matches the live preset's aspect before we resample to its
    // exact spec (a still-pending deferred frame could be the prior preset).
    // Fail loudly if it can't settle rather than export a stale-aspect raster.
    // Flip the loading state only AFTER the gate so it tracks the capture.
    const stabilized = await waitForStable(() => !previewStaleRef.current)
    if (!stabilized) {
      toast.error('Preview is still updating — pause a moment, then export again.')
      return
    }
    // Capture larger than the on-screen preview so the source raster is ≥ the
    // store target, then `exactSize` resamples to the exact spec (e.g. exactly
    // 1290×2796) — App Store Connect / Play Console reject off-by-one sizes.
    const previewWidth = previewRef.current.offsetWidth || 260
    const captureScale = Math.min(8, Math.max(2, Math.ceil(preset.w / previewWidth)))
    beginCapture()
    await captureAndDownload({
      element: previewRef.current,
      format: 'png',
      filename: `${preset.id}-${preset.w}x${preset.h}`,
      scale: captureScale,
      exactSize: { width: preset.w, height: preset.h },
      trackingSlug,
      engagementMs: getTotalEngagementMs(),
      extraParams: { preset: preset.id, store: preset.store, width: preset.w, height: preset.h },
      onSuccess: () => toast.success(`Exported at ${preset.w} × ${preset.h}px`),
      onError: () => toast.error('Failed to export. Please try again.'),
    })
  }), [runExport, getTotalEngagementMs, preset.id, preset.store, preset.w, preset.h, trackingSlug, previewStaleRef])

  const inputClasses =
    'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm transition-colors focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white'

  // Memoize the preview ELEMENT so the per-keystroke urgent re-render (deferred*
  // values referentially unchanged) reuses it and React bails out of re-rendering
  // the heavy store-screenshot preview — it then renders only on the low-priority
  // deferred pass. Without this, useDeferredValue double-renders and *raises* INP.
  const storeScreenshot = useMemo(
    () => <StoreScreenshot preset={previewPreset} state={deferredState} shot={deferredShot} />,
    [previewPreset, deferredState, deferredShot],
  )

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-1 sm:px-0 lg:flex-row lg:items-start">
      {/* ── Controls ─────────────────────────────────────────────── */}
      <section className="flex-1 rounded-3xl border border-slate-200/70 bg-white/80 p-6 shadow-2xl dark:border-slate-800/70 dark:bg-slate-900/70">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50">Design your store screenshot</h2>

        {/* Preset toggle — segmented-control grid (6 presets read as a tidy 3+3). */}
        <div className="mt-4">
          <div className="grid grid-cols-2 gap-1.5 rounded-2xl bg-slate-100 p-1.5 sm:grid-cols-3 dark:bg-slate-800/60">
            {PRESETS.map(p => (
              <button key={p.id} type="button" onClick={() => update('presetId', p.id)} aria-pressed={state.presetId === p.id}
                className={clsx('rounded-xl px-3 py-2 text-center text-sm font-medium transition',
                  state.presetId === p.id ? 'bg-white text-indigo-600 shadow-sm dark:bg-slate-900 dark:text-indigo-300' : 'text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white')}>
                {p.label}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            {preset.store} · target {preset.w} × {preset.h}px ({preset.banner ? 'feature graphic' : `${preset.w}:${preset.h} portrait`})
          </p>
        </div>

        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          <Field label="Headline" htmlFor="ss-headline" full>
            <input id="ss-headline" className={inputClasses} value={state.headline} maxLength={60}
              onChange={e => update('headline', e.target.value)} />
          </Field>
          <Field label="Subheadline (optional)" htmlFor="ss-sub" full>
            <input id="ss-sub" className={inputClasses} value={state.subheadline} maxLength={90}
              onChange={e => update('subheadline', e.target.value)} />
          </Field>

          {!preset.banner && (
            <Field label="Caption position" htmlFor="ss-pos">
              <select id="ss-pos" className={inputClasses} value={state.captionPos}
                onChange={e => update('captionPos', e.target.value as CaptionPos)}>
                <option value="top">Above the device</option>
                <option value="bottom">Below the device</option>
              </select>
            </Field>
          )}
          <Field label="Text color" htmlFor="ss-textColor">
            <input id="ss-textColor" type="color" className="h-[42px] w-full cursor-pointer rounded-lg border border-gray-300 dark:border-gray-600"
              value={state.textColor} onChange={e => update('textColor', e.target.value)} />
          </Field>

          <Field label="Background">
            <div className="flex h-[42px] flex-wrap items-center gap-2">
              {BACKGROUNDS.map(b => (
                <button key={b.id} type="button" aria-label={`Background ${b.label}`} aria-pressed={!state.useSolid && state.backgroundId === b.id}
                  onClick={() => { update('useSolid', false); update('backgroundId', b.id) }}
                  className={clsx('h-7 w-7 rounded-full border transition', !state.useSolid && state.backgroundId === b.id ? 'ring-2 ring-indigo-500 ring-offset-2 dark:ring-offset-slate-900' : 'border-black/10')}
                  style={{ background: b.css }} />
              ))}
            </div>
          </Field>
          <Field label="Or solid color">
            <div className="flex h-[42px] items-center gap-2">
              <input type="color" aria-label="Solid background color" className="h-9 w-12 cursor-pointer rounded border border-gray-300 dark:border-gray-600"
                value={state.solidColor} onChange={e => { update('solidColor', e.target.value); update('useSolid', true) }} />
              <label className="flex items-center gap-1.5 text-sm text-slate-700 dark:text-slate-200">
                <input type="checkbox" checked={state.useSolid} onChange={e => update('useSolid', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600" />
                Use solid
              </label>
            </div>
          </Field>

          <Field label="Screenshot / graphic">
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-600 transition hover:border-indigo-400 hover:text-indigo-600 dark:border-slate-600 dark:text-slate-300">
              <PhotoIcon className="h-5 w-5" />
              {shot ? 'Replace image' : 'Upload image'}
              <input type="file" accept="image/*" aria-label="Upload screenshot" className="sr-only" onChange={handleShot} />
            </label>
          </Field>
          {!preset.banner && preset.frame !== 'none' && (
            <Field label="Device frame">
              <label className="flex h-[42px] items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input type="checkbox" checked={state.showFrame} onChange={e => update('showFrame', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600" />
                Show device frame
              </label>
            </Field>
          )}
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-slate-200/70 pt-5 dark:border-slate-700/70">
          <ExportButton
            isExporting={isExporting}
            onExport={() => void exportPng()}
            className="rounded-full px-6 py-2 text-sm font-semibold text-white shadow-sm transition"
            idleClassName="bg-indigo-600 hover:bg-indigo-700"
            exportingClassName="cursor-not-allowed bg-indigo-400"
          />
          <button type="button" onClick={reset}
            className="flex items-center gap-1.5 rounded-full border border-slate-300/70 px-4 py-2 text-sm font-medium text-slate-700 transition hover:border-red-200 hover:text-red-600 dark:border-slate-600 dark:text-slate-200">
            <ArrowPathIcon className="h-4 w-4" /> Reset
          </button>
        </div>
      </section>

      {/* ── Preview ──────────────────────────────────────────────── */}
      <aside className="lg:sticky lg:top-24 lg:w-[26rem]">
        <p className="mb-3 text-center text-xs font-medium uppercase tracking-wide text-slate-400">Live preview</p>
        <div className="flex justify-center">
          <div className={clsx('w-full', previewPreset.banner ? 'max-w-[420px]' : 'max-w-[260px]')}>
            <div ref={previewRef} className="w-full overflow-hidden" style={{ aspectRatio: `${previewPreset.w} / ${previewPreset.h}`, background: previewBackground }}>
              {storeScreenshot}
            </div>
          </div>
        </div>
        <p className="mt-3 text-center text-xs text-slate-400">
          Exports at the exact {preset.store} {preset.label} resolution — {preset.w} × {preset.h}px — ready to upload.
        </p>
      </aside>
    </div>
  )
}

function Field({ label, htmlFor, full, children }: { label: string; htmlFor?: string; full?: boolean; children: React.ReactNode }): JSX.Element {
  const headingClass = 'block text-sm font-medium text-slate-700 dark:text-slate-200'
  return (
    <div className={clsx('space-y-1.5', full && 'sm:col-span-2')}>
      {htmlFor ? <label htmlFor={htmlFor} className={headingClass}>{label}</label> : <span className={headingClass}>{label}</span>}
      {children}
    </div>
  )
}

/** The composed store screenshot: caption + framed device (or caption-only banner).
 * Text uses fixed px sizes tuned for the on-screen preview width (phones render
 * at ~260px, the banner at ~420px); html2canvas scales the whole capture 2× on
 * export, so proportions are preserved. (Fixed sizes are used deliberately over
 * container-query units, which html2canvas can't resolve reliably.) */
function StoreScreenshot({ preset, state, shot }: { preset: Preset; state: ScreenshotState; shot: string | null }): JSX.Element {
  const caption = (
    <div className="px-[7%] text-center" style={{ color: state.textColor }}>
      <div className="text-[15px] font-bold leading-tight">{state.headline}</div>
      {state.subheadline.trim() && <div className="mt-1 text-[10px] font-medium leading-snug opacity-90">{state.subheadline}</div>}
    </div>
  )

  // Feature graphic: caption-only landscape banner, optional graphic on the right.
  if (preset.banner) {
    return (
      <div className="flex h-full w-full items-center justify-between gap-3 px-[6%]">
        <div className="text-left" style={{ color: state.textColor }}>
          <div className="text-[22px] font-extrabold leading-tight">{state.headline}</div>
          {state.subheadline.trim() && <div className="mt-1 text-[12px] font-medium leading-snug opacity-90">{state.subheadline}</div>}
        </div>
        {shot && (
          // eslint-disable-next-line @next/next/no-img-element -- user-uploaded data URL; html2canvas needs the raw <img>
          <img src={shot} alt="Feature graphic" className="h-[70%] w-[34%] flex-shrink-0 rounded-xl object-cover shadow-lg" />
        )}
      </div>
    )
  }

  const device = <DeviceFrame frame={state.showFrame ? preset.frame : 'none'} image={shot} aspect={preset.w / preset.h} />

  return (
    <div className="flex h-full w-full flex-col items-center justify-between py-[6%]">
      {state.captionPos === 'top' ? (
        <>
          <div className="w-full">{caption}</div>
          <div className="flex min-h-0 flex-1 items-end justify-center pt-3">{device}</div>
        </>
      ) : (
        <>
          <div className="flex min-h-0 flex-1 items-start justify-center pb-3">{device}</div>
          <div className="w-full">{caption}</div>
        </>
      )}
    </div>
  )
}

/** Device frame: an iPhone (Dynamic Island), an Android punch-hole, or a bare
 * rounded screen (iPad / "no frame"). The uploaded screenshot fills the screen.
 * `aspect` (width ÷ height) comes from the chosen store preset so the framed
 * screen matches the screenshot's real shape — a hardcoded phone ratio would
 * crop the 9:16 presets (Play phone, iPhone 5.5") and squash the 3:4 iPad.
 *
 * The device box derives its width purely from `aspectRatio` (no explicit
 * width). Unlike AdMockup's `CreativeSlot` — which avoids `aspect-ratio` on
 * export because its only children are absolutely positioned — this box's screen
 * child is IN-FLOW (`h-full w-full`), so html2canvas resolves a real width on
 * export. Verified: exported Play-phone (framed Android) and iPad (frameless
 * 3:4) PNGs render the device at correct proportions, not collapsed. */
function DeviceFrame({ frame, image, aspect }: { frame: FrameKind; image: string | null; aspect: number }): JSX.Element {
  const screen = image ? (
    // eslint-disable-next-line @next/next/no-img-element -- user-uploaded data URL; html2canvas needs the raw <img>
    <img src={image} alt="App screenshot" className="h-full w-full object-cover" />
  ) : (
    <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 bg-white/15 text-center text-white/70">
      <PhotoIcon className="h-6 w-6" />
      <span className="text-[9px]">Upload your screenshot</span>
    </div>
  )

  // Corner radius must be EQUAL on both axes for a real device shape. A single
  // `%` radius resolves to that % of width horizontally but that % of HEIGHT
  // vertically — on a tall phone that stretches the corners into an angled
  // ellipse. The `H% / V%` form lets us set the vertical % to `H × aspect` so
  // both axes land on the same pixel radius (H% of width = H·aspect% of height).
  const r = (h: number): string => `${h}% / ${(h * aspect).toFixed(2)}%`

  if (frame === 'none') {
    return <div className="relative h-full max-h-full max-w-full overflow-hidden shadow-2xl" style={{ aspectRatio: aspect, borderRadius: r(8) }}>{screen}</div>
  }
  if (frame === 'android') {
    return (
      <div className="relative h-full max-h-full max-w-full overflow-hidden border-[3px] border-black bg-black shadow-2xl" style={{ aspectRatio: aspect, borderRadius: r(13) }}>
        <div className="absolute left-1/2 top-[1.5%] z-10 h-[1.6%] -translate-x-1/2 rounded-full bg-black ring-1 ring-white/20" style={{ aspectRatio: '1' }} />
        <div className="h-full w-full overflow-hidden" style={{ borderRadius: r(11) }}>{screen}</div>
      </div>
    )
  }
  // iphone — Dynamic Island + true rounded-rectangle corners
  return (
    <div className="relative h-full max-h-full max-w-full overflow-hidden border-[5px] border-black bg-black shadow-2xl" style={{ aspectRatio: aspect, borderRadius: r(15) }}>
      <div className="absolute left-1/2 top-[1.6%] z-10 h-[2.4%] w-[30%] -translate-x-1/2 rounded-full bg-black" />
      <div className="h-full w-full overflow-hidden" style={{ borderRadius: r(13) }}>{screen}</div>
    </div>
  )
}
